import { UtilsService } from 'core/utils/utils.service';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { StorageValues } from 'src/app/modules/shared/entities/storage-values.enum';
import { Subscription } from 'rxjs';
import { DashboardTemplateValues } from 'src/app/modules/shared/entities/dashboard-template-values.class';
import { DashboardService } from '../../../services/dashboard.service';

@Component({
  selector: 'app-projects-search',
  templateUrl: './projects-search.component.html',
  styleUrls: ['./projects-search.component.scss'],
  standalone: false
})
export class ProjectsSearchComponent implements OnInit, OnDestroy {
  appStrings: AppLocalization;
  isMobile = false;
  isAccountManager = false;
  accountManagerId: string;
  projectsSearchForm: any;
  isSelectState = false;
  accountManagers: { value: string, label: string }[] = [];
  statuses: { value: number, label: string }[] = [];
  cachedSearchValues: any;
  disableButtons = true;
  buttons = [
    { label: 'Clear all filters', type: 'tertiary', disableEmpty: true },
    { label: 'Search', type: 'primary' },
  ];
  dialogData: any;
  loading = true;
  subs = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<ProjectsSearchComponent>,
    private appInfoService: AppInfoService,
    private fb: FormBuilder,
    private dashboardService: DashboardService,
    private utilsService: UtilsService,
    private deviceService: DeviceDetectorService,
    @Inject(MAT_DIALOG_DATA) data,
  ) {
    this.dialogData = data;
  }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.isMobile = this.deviceService.isMobile();
    this.isAccountManager = this.appInfoService.isAccountManager();
    this.cachedSearchValues = JSON.parse(sessionStorage.getItem(StorageValues.CachedFilters +
      (this.dialogData.isHistory ? StorageValues.ProjectsHistoryKey : StorageValues.ProjectsKey)));
    this.dashboardService.getStaticValues((templateValues: DashboardTemplateValues) => {
      this.statuses = this.getStatuses(this.utilsService.mapNumberKeyValueOptions(templateValues.ProjectStatus));
    });
    this.dashboardService.getFollowupAccountManagers((list: AccountManager[]) => {
      this.accountManagers = this.utilsService.mapObjectToSelect(list, 'id', 'name');
      this.projectsSearchForm = this.fb.group({
        projectOwnerId: [this.isAccountManager ? this.dialogData.accountManagerId : this.cachedSearchValues?.projectOwnerId ?? null],
        companyName: [this.cachedSearchValues?.companyName ?? ''],
        projectName: [this.cachedSearchValues?.projectName ?? null],
        contactName: [this.cachedSearchValues?.contactName ?? null],
        statusId: [this.cachedSearchValues?.statusId ?? null],
        displayedSearchValue: [null]
      });
      this.loading = false;

      this.updateButtonsState();
      this.subs.add(this.projectsSearchForm.valueChanges.subscribe(() => {
        this.updateButtonsState();
      }));
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  updateButtonsState() {
    const form = this.projectsSearchForm.value;
    if (this.isAccountManager) {
      delete form.projectOwnerId;
    }
    this.disableButtons = !this.utilsService.hasAnyValue(form);
  }

  btnCallback(i: number) {
    if (i === 0) {
      if (this.isAccountManager) {
        this.projectsSearchForm.controls.companyName.reset();
        this.projectsSearchForm.controls.projectName.reset();
        this.projectsSearchForm.controls.contactName.reset();
        this.projectsSearchForm.controls.statusId.reset();
      } else {
        this.projectsSearchForm.reset();
      }
    } else {
      this.normalizeInputs();
      const displayValue = this.formatDisplayValue();
      this.projectsSearchForm.controls.displayedSearchValue.setValue(displayValue);
      const storageKey = this.dialogData.isHistory ? StorageValues.ProjectsHistoryKey : StorageValues.ProjectsKey;
      sessionStorage.setItem(StorageValues.CachedFilters + storageKey, JSON.stringify(this.projectsSearchForm.value));
      this.dialogRef.close({ performSearch: true, searchDisplay: displayValue });
    }
  }

  formatDisplayValue() {
    let displayValue = '';
    Object.keys(this.projectsSearchForm.value).forEach((item) => {
      const value = this.projectsSearchForm.value[item];
      if (value && value !== '') {
        if (item === 'projectOwnerId') {
          displayValue += this.accountManagers.find(am => am.value === value)?.label + '; ';
        } else if (item === 'statusId') {
          displayValue += this.statuses.find(am => am.value === value)?.label + '; ';
        } else if (item !== 'displayedSearchValue') {
          displayValue += value + '; ';
        }
      }
    });
    if (!displayValue.length) {
      return this.appStrings.accounts_search;
    }
    return displayValue;
  }

  normalizeInputs() {
    const projectOwnerControl = this.projectsSearchForm.controls.projectOwnerId;
    if (projectOwnerControl.value === '' || !this.accountManagers.find(am => am.value === projectOwnerControl.value)) {
      projectOwnerControl.setValue(null);
    }
  }

  private getStatuses(statuses: { value: number, label: any }[]) {
    let displayList = [];
    if (this.dialogData.isHistory) {
      displayList = this.dashboardService.historyProjectsStatus;
    } else {
      displayList = this.dashboardService.projectsStatus;
    }

    return statuses.filter(status => displayList.includes(status.value));
  }
}
